/**
 * 9개 산업안전 AI 기능 SEO 공개 랜딩 wrapper 모음.
 *
 * 각 페이지는 FeaturePage 공통 템플릿에 id/slug/accent/icons/related 만 다르게 전달.
 * URL: /features/{slug}
 *
 * SEO 설계:
 *   - 각 페이지별 독립 <title>·meta·canonical·FAQPage·SoftwareApplication JSON-LD
 *   - 한·영 i18n 카피는 featurePages.{id}.* 키에서 자동 로드
 *   - 사이드 cross-link(related) 로 내부 링크 구조 강화
 */
import {
  FiClipboard, FiFileText, FiBookOpen, FiAlertTriangle, FiShield,
  FiMessageSquare, FiCamera, FiCheckSquare,
  FiZap, FiCpu, FiRefreshCw, FiClock, FiLock, FiGlobe,
  FiAward, FiLayers, FiActivity, FiEdit3, FiSearch, FiVideo,
  FiHeadphones, FiTrendingUp, FiMap, FiTarget, FiPrinter,
  FiUploadCloud, FiCornerUpRight, FiAlertOctagon, FiUsers,
  FiBell, FiArchive,
  FiGitBranch, FiTrello, FiDollarSign, FiBarChart2, FiPercent,
  FiBookmark, FiSliders, FiTool,
} from 'react-icons/fi'
import FeaturePage, { type FeaturePageProps } from '../../../components/feature/FeaturePage'
import SafetyEducationFeature from './SafetyEducationFeature'

// ── related 카드 4개 묶음을 cross-link 용으로 슬러그→i18n title 매핑 ──
const RELATED_LABELS: Record<string, string> = {
  jsa: 'featurePages.jsa.heroBadge',
  tbm: 'featurePages.tbm.heroBadge',
  'safety-education': 'featurePages.safetyEducation.heroBadge',
  'work-permit': 'featurePages.workPermit.heroBadge',
  'accident-report': 'featurePages.accidentReport.heroBadge',
  chatbot: 'featurePages.chatbot.heroBadge',
  'risk-assessment': 'featurePages.riskAssessment.heroBadge',
  'image-analysis': 'featurePages.imageAnalysis.heroBadge',
  corrective: 'featurePages.corrective.heroBadge',
  eta: 'featurePages.eta.heroBadge',
  fta: 'featurePages.fta.heroBadge',
  'safety-cost': 'featurePages.safetyCost.heroBadge',
}

const buildRelated = (excludeSlug: string, slugs: string[]): FeaturePageProps['related'] =>
  slugs
    .filter((s) => s !== excludeSlug)
    .slice(0, 3)
    .map((s) => ({ slug: s, labelKey: RELATED_LABELS[s]! }))

// ── 1) JSA — 작업안전분석서 ──────────────────────────────────────
export function FeatureJSA() {
  return (
    <FeaturePage
      id="jsa"
      slug="jsa"
      HeroIcon={FiClipboard}
      accent="violet"
      featureIcons={[FiLayers, FiTarget, FiSearch, FiPrinter, FiGlobe, FiArchive]}
      related={buildRelated('jsa', ['risk-assessment', 'tbm', 'work-permit', 'accident-report'])}
    />
  )
}

// ── 2) TBM — 툴박스 미팅 ────────────────────────────────────────
export function FeatureTBM() {
  return (
    <FeaturePage
      id="tbm"
      slug="tbm"
      HeroIcon={FiFileText}
      accent="amber"
      featureIcons={[FiEdit3, FiCamera, FiPrinter, FiRefreshCw, FiActivity, FiUploadCloud]}
      related={buildRelated('tbm', ['safety-education', 'jsa', 'work-permit'])}
    />
  )
}

// ── 3) 안전교육 — 전용 개편 페이지 (14종 법정교육 + 39종 특별교육 · 5개 언어) ──
export function FeatureSafetyEducation() {
  return <SafetyEducationFeature />
}

// ── 4) 작업허가서 — PTW ────────────────────────────────────────
export function FeatureWorkPermit() {
  return (
    <FeaturePage
      id="workPermit"
      slug="work-permit"
      HeroIcon={FiAlertOctagon}
      accent="orange"
      featureIcons={[FiLayers, FiEdit3, FiCheckSquare, FiBookOpen, FiPrinter, FiClock]}
      related={buildRelated('work-permit', ['jsa', 'risk-assessment', 'corrective'])}
    />
  )
}

// ── 5) 산업재해조사표 — 별지 30호 ───────────────────────────────
export function FeatureAccidentReport() {
  return (
    <FeaturePage
      id="accidentReport"
      slug="accident-report"
      HeroIcon={FiAlertTriangle}
      accent="red"
      featureIcons={[FiTarget, FiSearch, FiTrendingUp, FiShield, FiPrinter, FiArchive]}
      related={buildRelated('accident-report', ['corrective', 'risk-assessment', 'image-analysis'])}
    />
  )
}

// ── 6) 챗봇 ─────────────────────────────────────────────────────
export function FeatureChatbot() {
  return (
    <FeaturePage
      id="chatbot"
      slug="chatbot"
      HeroIcon={FiMessageSquare}
      accent="blue"
      featureIcons={[FiBookOpen, FiZap, FiRefreshCw, FiSearch, FiUsers, FiGlobe]}
      related={buildRelated('chatbot', ['image-analysis', 'jsa', 'safety-education'])}
    />
  )
}

// ── 7) 위험성평가 — 산안법 §36 ──────────────────────────────────
export function FeatureRiskAssessment() {
  return (
    <FeaturePage
      id="riskAssessment"
      slug="risk-assessment"
      HeroIcon={FiShield}
      accent="indigo"
      featureIcons={[FiSearch, FiTarget, FiMap, FiPrinter, FiCpu, FiArchive]}
      related={buildRelated('risk-assessment', ['jsa', 'work-permit', 'accident-report'])}
    />
  )
}

// ── 8) 이미지 위험분석 ──────────────────────────────────────────
export function FeatureImageAnalysis() {
  return (
    <FeaturePage
      id="imageAnalysis"
      slug="image-analysis"
      HeroIcon={FiCamera}
      accent="emerald"
      featureIcons={[FiTarget, FiBookOpen, FiTrendingUp, FiCornerUpRight, FiSearch, FiBell]}
      related={buildRelated('image-analysis', ['corrective', 'accident-report', 'chatbot'])}
    />
  )
}

// ── 9) 시정지시서 ───────────────────────────────────────────────
export function FeatureCorrective() {
  return (
    <FeaturePage
      id="corrective"
      slug="corrective"
      HeroIcon={FiCheckSquare}
      accent="rose"
      featureIcons={[FiFileText, FiTarget, FiClock, FiShield, FiCornerUpRight, FiCamera]}
      related={buildRelated('corrective', ['image-analysis', 'work-permit', 'accident-report'])}
    />
  )
}

// ── 10) ETA — 사건수 분석 ────────────────────────────────────────
export function FeatureETA() {
  return (
    <FeaturePage
      id="eta"
      slug="eta"
      HeroIcon={FiGitBranch}
      accent="amber"
      featureIcons={[FiTrendingUp, FiSliders, FiBarChart2, FiTarget, FiActivity, FiArchive]}
      related={buildRelated('eta', ['fta', 'risk-assessment', 'jsa', 'safety-cost'])}
    />
  )
}

// ── 11) FTA — 결함수 분석 ────────────────────────────────────────
export function FeatureFTA() {
  return (
    <FeaturePage
      id="fta"
      slug="fta"
      HeroIcon={FiTrello}
      accent="rose"
      featureIcons={[FiPercent, FiTool, FiBarChart2, FiTarget, FiActivity, FiArchive]}
      related={buildRelated('fta', ['eta', 'risk-assessment', 'image-analysis', 'safety-cost'])}
    />
  )
}

// ── 12) 산업안전보건관리비 계상 ───────────────────────────────────
export function FeatureSafetyCost() {
  return (
    <FeaturePage
      id="safetyCost"
      slug="safety-cost"
      HeroIcon={FiDollarSign}
      accent="emerald"
      featureIcons={[FiBookmark, FiSliders, FiBarChart2, FiArchive, FiGlobe, FiPrinter]}
      related={buildRelated('safety-cost', ['eta', 'fta', 'work-permit', 'accident-report'])}
    />
  )
}

// 한 곳에서 default export 로 라우터에 일괄 매핑할 수 있도록 묶어 노출
export const FEATURE_PAGES = {
  jsa: FeatureJSA,
  tbm: FeatureTBM,
  'safety-education': FeatureSafetyEducation,
  'work-permit': FeatureWorkPermit,
  'accident-report': FeatureAccidentReport,
  chatbot: FeatureChatbot,
  'risk-assessment': FeatureRiskAssessment,
  'image-analysis': FeatureImageAnalysis,
  corrective: FeatureCorrective,
  eta: FeatureETA,
  fta: FeatureFTA,
  'safety-cost': FeatureSafetyCost,
} as const

export type FeatureSlug = keyof typeof FEATURE_PAGES
